import React, { useEffect, useReducer } from "react";
import { type RouteComponentProps } from "react-router-dom";
import { FirebaseError } from "firebase/app";

import LoadingScreen from "../../components/Loading";
import { CertificateError, CertificateFetchStatus } from "../../firebase/api";
import { useFirebaseContext } from "../../firebase/FirebaseContext";
import { ProseDateFormat, joinWithoutOxfordComma } from "../../utils/format";
import {
  getCertificationDate,
  getCertificationExpirationDate,
  getDbaBusinessName,
  getNaicsCodes,
  getOwnerNames,
  getRegisteredBusinessName,
  type NaicsCode,
  type RawDocument,
} from "../../utils/selectors";

import { printCertificate } from "./download-utils";

import "./certificate.css";

export const PRINT_CONTAINER_ID = "divToPrint";

interface Props {
  /** The original certification date. */
  certificationDate?: Date | null;
  /** The ID no. for this certificate. */
  certificateNumber?: string;
  /** The certification expiration date. */
  expirationDate?: Date | null;
  /** The business owner names. */
  ownerNames?: string[];
  /** The business NAICs codes. */
  naicsCodes?: NaicsCode[];
  /** The business name. */
  businessName?: string;
}

function DisplayCertificate({
  businessName,
  certificateNumber,
  certificationDate,
  expirationDate,
  naicsCodes,
  ownerNames,
}: Props) {
  return (
    <div className="certificate-printing">
      <div className="cert-print-boundary" id={PRINT_CONTAINER_ID}>
        <div className="certificatebox">
          <div className="boxBtm">
            <div className="yellow-box">
              <div className="yellow-boxBtm">
                <div className="blue-box">
                  <div className="blue-boxBtm">
                    <div className="subtitle-box1">
                      <img src="/assets/images/logo.png" alt="logo" />
                    </div>
                    <div className="card-box">
                      <img
                        src="/assets/images/certificate/ribbon.jpg"
                        className="card-img"
                        alt="ribbon"
                      />
                      <p className="card-title">CERTIFIED BLACK-OWNED</p>
                      <p className="card-subtitle">
                        THIS CERTIFICATE IS HEREBY APPOINTED TO:
                      </p>
                    </div>
                    <p className="title2">{businessName}</p>
                    <div className="desc-box">
                      <p className="desc1">
                        And its owner(s),{" "}
                        {joinWithoutOxfordComma(ownerNames ?? [])}. It is hereby
                        certified as a Black-Owned and Operated enterprise as
                        defined by U.S. Black Chambers, Inc. and its national
                        affiliates, at the time of this certificate award, deem
                        this business worthy of all contracts and opportunities
                        available to a minority-owned business.
                      </p>
                      <div className="desc-subbox1">
                        <p className="desc-subbox1-left">
                          {certificationDate && (
                            <span>
                              Issued on:{" "}
                              {ProseDateFormat.format(certificationDate)}
                            </span>
                          )}
                          <span>Certification Number: {certificateNumber}</span>
                          {expirationDate && (
                            <span>
                              Expiration Date:{" "}
                              {ProseDateFormat.format(expirationDate)}
                            </span>
                          )}
                        </p>

                        <div className="desc-subbox1-right">
                          <p className="naic-code">
                            <span>
                              NAICS Code(s):{" "}
                              {joinWithoutOxfordComma(
                                (naicsCodes ?? []).map(({ code }) => code)
                              )}
                            </span>
                          </p>
                          <p>
                            <span className="desc">
                              Description of their products/services
                            </span>
                            <span className="desc-subtitle">
                              {" "}
                              as defined by the North American Industry
                              classification System (NAICS)
                            </span>
                          </p>
                        </div>
                      </div>
                      <div className="desc-subbox2">
                        <img
                          src="/assets/images/certificate/signature.png"
                          className="signature"
                          alt="signature"
                        />
                        <div className="desc-subbox2-sign"></div>
                        <p className="desc-subbox2-name">Ron Busby, Sr.</p>
                        <p className="desc-subbox2-copyright">
                          President CEO, U.S Black Chambers, Inc
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="certificationBadge">
          <img src="/assets/images/badge-new.png" alt="badge" />
        </div>
      </div>
    </div>
  );
}

enum CertificateNameType {
  DBA = "DBA",
  REGISTERED = "REGISTERED",
}

enum CertificateFetchActionKind {
  REQUEST = "REQUEST",
  RETRIEVE = "RETRIEVE",
  FAILED = "FAILED",
}

type CertificateFetchAction =
  | { type: CertificateNameType.DBA }
  | { type: CertificateNameType.REGISTERED }
  | { type: CertificateFetchActionKind.REQUEST }
  | { type: CertificateFetchActionKind.RETRIEVE; doc: RawDocument }
  | {
      type: CertificateFetchActionKind.FAILED;
      failure:
        | CertificateFetchStatus.NOT_AUTHORIZED
        | CertificateFetchStatus.NOT_FOUND
        | CertificateFetchStatus.NOT_READY
        | CertificateFetchStatus.UNKNOWN_FAILURE;
      ctx?: string;
    };

interface CertificateFetchState {
  /** The current fetch status. */
  status: CertificateFetchStatus;
  /** Business name selector. */
  getName: (doc: RawDocument) => string;
  /** The document data. */
  doc?: RawDocument;
  /** The context. */
  ctx?: string;
}

function certificateReducer(
  state: CertificateFetchState,
  action: CertificateFetchAction
) {
  switch (action.type) {
    case CertificateNameType.DBA: {
      return { ...state, getName: getDbaBusinessName };
    }
    case CertificateNameType.REGISTERED: {
      return { ...state, getName: getRegisteredBusinessName };
    }
    case CertificateFetchActionKind.RETRIEVE: {
      const { doc } = action;
      return { ...state, status: CertificateFetchStatus.FETCHED, doc };
    }
    case CertificateFetchActionKind.FAILED: {
      const { failure, ctx } = action;
      return { ...state, status: failure, ctx };
    }
    case CertificateFetchActionKind.REQUEST:
      return { ...state, status: CertificateFetchStatus.PENDING };
    default:
      return state;
  }
}

interface ErrorPageProps {
  children: React.ReactNode;
}

/** The error page. */
function ErrorPage({ children }: ErrorPageProps) {
  return (
    <div className="error-page">
      <div className="error-page-content">
        <p>{children}</p>
        <button className="cert-hyperlink" onClick={() => window.close()}>
          Feel free to close this window.
        </button>
      </div>
    </div>
  );
}

/** The display certificate. */
export default function CertificatePage({
  match,
}: RouteComponentProps<{ certificateNumber: string }>) {
  const [state, dispatch] = useReducer(certificateReducer, {
    status: CertificateFetchStatus.PENDING,
    getName: getRegisteredBusinessName,
  });
  const {
    api: { getDocumentFromCertificateNumber },
  } = useFirebaseContext();
  const certificateNumber = match.params.certificateNumber;

  // Sequence data query from firebase on the basis of certificate number
  useEffect(() => {
    getDocumentFromCertificateNumber(certificateNumber)
      .then((doc) =>
        dispatch({ type: CertificateFetchActionKind.RETRIEVE, doc })
      )
      .catch((err) => {
        if (err instanceof CertificateError) {
          return dispatch({
            type: CertificateFetchActionKind.FAILED,
            failure: err.failure,
          });
        }
        if (err instanceof FirebaseError && err.code === "permission-denied") {
          return dispatch({
            type: CertificateFetchActionKind.FAILED,
            failure: CertificateFetchStatus.NOT_AUTHORIZED,
          });
        }
        return dispatch({
          type: CertificateFetchActionKind.FAILED,
          failure: CertificateFetchStatus.UNKNOWN_FAILURE,
          ctx: err instanceof Error ? err.message : err.toString(),
        });
      });
  }, [certificateNumber, dispatch, getDocumentFromCertificateNumber]);

  // No certificate available
  if (
    state.status === CertificateFetchStatus.NOT_FOUND ||
    state.status === CertificateFetchStatus.NOT_READY
  ) {
    console.error(
      "This business is ineligible for a ByBlack certificate",
      state.status
    );
    return (
      <ErrorPage>
        Our records indicate that this business is not yet eligible for a
        ByBlack certificate. If you believe this to be incorrect, please reach
        out to{" "}
        <a href="mailto:certification@usblackchambers.org">
          certification@usblackchambers.org
        </a>{" "}
        for assistance.
      </ErrorPage>
    );
  }

  // User is not authorized to view the certificate
  if (state.status === CertificateFetchStatus.NOT_AUTHORIZED) {
    console.error("User is not authorized to view the certificate.");
    return (
      <ErrorPage>
        Our records you are ineligible to view this business' certificate. If
        you believe this to be incorrect, please confirm the certification id
        number is your own, and reach out to{" "}
        <a href="mailto:certification@usblackchambers.org">
          certification@usblackchambers.org
        </a>{" "}
        for assistance.
      </ErrorPage>
    );
  }

  // Something went wrong
  if (state.status === CertificateFetchStatus.UNKNOWN_FAILURE) {
    console.error("Something unexpected went wrong!", state.ctx);
    return (
      <ErrorPage>
        Something went wrong when attempting to retrieve this certificate.
        Please reach out to
        <a href="mailto:certification@usblackchambers.org">
          certification@usblackchambers.org
        </a>{" "}
        for assistance or try again.
      </ErrorPage>
    );
  }

  // Retrieving the certificate
  if (state.status === CertificateFetchStatus.PENDING) {
    return <LoadingScreen />;
  }

  // Generate the certificate
  const data = state.doc!;
  const isRegisteredName = state.getName === getRegisteredBusinessName;
  const hasDBA = !!getDbaBusinessName(data);
  return (
    <div className="certificate-screen">
      <div className="download-button-container">
        <button
          className="navbar-button cert-download-btn"
          onClick={printCertificate}
        >
          Download Certificate
        </button>
      </div>
      <DisplayCertificate
        businessName={state.getName(data)}
        certificateNumber={certificateNumber}
        certificationDate={getCertificationDate(data)!}
        expirationDate={getCertificationExpirationDate(data)}
        naicsCodes={getNaicsCodes(data)}
        ownerNames={getOwnerNames(data)}
      />
      {hasDBA && (
        <button
          className="convert-button"
          // Allow users to configure DBA on certificate
          onClick={() =>
            dispatch({
              type: isRegisteredName
                ? CertificateNameType.DBA
                : CertificateNameType.REGISTERED,
            })
          }
        >
          Need your certificate to reflect your
          {isRegisteredName ? " DBA " : " legally registered "}
          business name? <span>Look no further.</span>
        </button>
      )}
    </div>
  );
}
